import { motion } from "framer-motion";
import {
  Bell,
  Download,
  FileText,
  MapPin,
  Plane,
  Radar,
  ScanLine,
  Shield,
  Upload,
  Users,
} from "lucide-react";
import { StoreButtons } from "@/components/landing/StoreButtons";
import { EditorialBrandImage } from "@/components/landing/EditorialBrandImage";
import { pages } from "@/content/pages";
import type { Locale } from "@/i18n/locales";

type AppViewProps = {
  locale: Locale;
};

const icons = [
  Plane,
  MapPin,
  FileText,
  Users,
  Radar,
  ScanLine,
  Download,
  Upload,
  Bell,
  Shield,
];

export function AppView({ locale }: AppViewProps) {
  const copy = pages[locale].app;

  return (
    <main className="bg-background">
      <section className="relative overflow-hidden pb-16 pt-36">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -right-40 -top-40 h-[500px] w-[500px] rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -left-32 top-60 h-[300px] w-[300px] rounded-full bg-accent/8 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-6xl px-5">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="mb-4 text-xs font-bold uppercase tracking-widest text-primary">
                {copy.eyebrow}
              </p>
              <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight text-[#1a3a5c] md:text-6xl">
                {copy.title}
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
                {copy.intro}
              </p>
              <StoreButtons locale={locale} className="mt-10" />
            </div>
            <img
              src="/images/vaxy-app.webp"
              alt={copy.heroImageAlt}
              width={1200}
              height={1200}
              loading="lazy"
              className="w-full rounded-3xl shadow-lg shadow-[#1a3a5c]/10"
            />
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-6xl px-5">
          <div className="mb-14 text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">
              {copy.insideEyebrow}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold text-[#1a3a5c] md:text-4xl">
              {copy.insideTitle}
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <article className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
              <EditorialBrandImage className="aspect-[4/3] w-full object-cover" />
            </article>
            {copy.features.map((feature, index) => {
              const Icon = icons[index] ?? Plane;
              return (
                <motion.article
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="rounded-2xl border border-border/60 bg-card p-6 transition-all hover:border-primary/30 hover:shadow-md"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="mb-2 font-heading text-base font-semibold text-[#1a3a5c]">
                    {feature.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {feature.description}
                  </p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#1a3a5c] py-20">
        <div className="mx-auto max-w-2xl px-5 text-center">
          <img
            src="/icon-ui.webp"
            alt="Vaxy"
            width={192}
            height={192}
            className="mx-auto mb-6 h-20 w-20 rounded-2xl shadow-xl"
          />
          <h2 className="mb-4 font-heading text-3xl font-bold text-white md:text-4xl">
            {copy.downloadTitle}
          </h2>
          <p className="mb-8 text-lg leading-relaxed text-white/70">
            {copy.downloadText}
          </p>
          <StoreButtons locale={locale} className="justify-center" />
        </div>
      </section>
    </main>
  );
}
